import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Tab, Card, Button } from 'react-bootstrap';
import { FaFileContract, FaUsers, FaChartLine, FaPlus } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import TransactionDashboard from './TransactionDashboard';
import TransactionDetails from './TransactionDetails';
import NewTransactionEnhanced from './NewTransactionEnhanced';
import EnhancedAgentDashboard from './EnhancedAgentDashboard';

// Placeholder for the new analytics dashboard
const AgentAnalyticsDashboard = () => {
  return (
    <Container>
      <h2 className="mb-4">Agent Performance Dashboard</h2>
      <Row>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body className="text-center">
              <h3>24</h3>
              <p className="text-muted mb-0">Active Clients</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body className="text-center">
              <h3>$2.4M</h3>
              <p className="text-muted mb-0">Pipeline Value</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body className="text-center">
              <h3>87%</h3>
              <p className="text-muted mb-0">Close Rate</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-3">
            <Card.Body className="text-center">
              <h3>12</h3>
              <p className="text-muted mb-0">Closed This Month</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="text-center mt-5">
        <p className="text-muted">Full Book of Business Analytics Coming Soon!</p>
      </div>
    </Container>
  );
};

const AgentPortalStreamlined = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract tab and transaction ID from URL path
  const getTabFromPath = () => {
    const pathParts = location.pathname.split('/');
    const tabIndex = pathParts.indexOf('agent-portal') + 1;
    const validTabs = ['dashboard', 'transactions', 'referrals', 'analytics'];
    const urlTab = pathParts[tabIndex];
    
    return validTabs.includes(urlTab) ? urlTab : 'dashboard';
  };
  
  // Extract transaction ID from URL if present
  const getTransactionIdFromPath = () => {
    const pathMatch = location.pathname.match(/\/agent-portal\/transactions\/([^/]+)/);
    // Check if the path is for creating a new transaction
    if (pathMatch && pathMatch[1] === 'new') {
      return null; // Return null to indicate no transaction ID
    }
    return pathMatch ? pathMatch[1] : null;
  };
  
  // Check if the URL indicates we should show the new transaction form
  const shouldShowNewTransaction = () => {
    return location.pathname === '/agent-portal/transactions/new';
  };
  
  const [activeKey, setActiveKey] = useState(getTabFromPath());
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(getTransactionIdFromPath());
  const [showNewTransaction, setShowNewTransaction] = useState(shouldShowNewTransaction());
  
  // Update tab and transaction ID when URL changes (browser back/forward)
  useEffect(() => {
    const newTab = getTabFromPath();
    const newTransactionId = getTransactionIdFromPath();
    const shouldShowNew = shouldShowNewTransaction();
    
    if (newTab !== activeKey) {
      setActiveKey(newTab);
    }
    
    // Handle new transaction route
    if (shouldShowNew) {
      setShowNewTransaction(true);
      setSelectedTransactionId(null);
    } else if (newTransactionId !== selectedTransactionId) {
      setSelectedTransactionId(newTransactionId);
      setShowNewTransaction(false);
    } else if (!newTransactionId && !shouldShowNew) {
      // No transaction ID and not showing new - reset both
      setSelectedTransactionId(null);
      setShowNewTransaction(false);
    }
  }, [location.pathname]);

  const handleTransactionSelect = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setShowNewTransaction(false);
  };

  const handleBackToTransactions = () => {
    setSelectedTransactionId(null);
    setShowNewTransaction(false);
  };

  const handleNewTransaction = () => {
    navigate('/agent-portal/transactions/new');
    setSelectedTransactionId(null);
    setShowNewTransaction(true);
    setActiveKey('transactions');
  };

  const handleTransactionCreated = (transactionId: string) => {
    setShowNewTransaction(false);
    setSelectedTransactionId(transactionId);
  };

  return (
    <Container fluid className="py-3">
      <Tab.Container 
        activeKey={activeKey} 
        onSelect={(key) => {
          const newTab = key || 'dashboard';
          setActiveKey(newTab);
          
          // Update URL to reflect the selected tab
          const basePath = '/agent-portal';
          const newPath = newTab === 'dashboard' ? basePath : `${basePath}/${newTab}`;
          navigate(newPath);
        }}
      >
        {/* Header with Streamlined Navigation */}
        <div 
          className="agent-portal-header mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(0, 212, 255, 0.15)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)'
          }}
        >
          <Row className="align-items-center">
            <Col md={12}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #00d4ff 0%, #667eea 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px'
                    }}
                  >
                    <FaUsers style={{ fontSize: '20px', color: 'white' }} />
                  </div>
                  <h4 className="mb-0" style={{ fontWeight: 600, color: '#1f2937' }}>
                    Agent Portal
                  </h4>
                </div>
                
                {/* Quick Action Button - Always visible */}
                {activeKey === 'transactions' && !showNewTransaction && !selectedTransactionId && (
                  <Button 
                    variant="primary" 
                    onClick={handleNewTransaction}
                    className="d-flex align-items-center"
                  >
                    <FaPlus className="me-2" />
                    New Transaction
                  </Button>
                )}
              </div>
              
              {/* Main Navigation - Dashboard and Transactions Only */}
              <Nav variant="pills" className="flex-row">
                <Nav.Item>
                  <Nav.Link 
                    eventKey="dashboard"
                    className="py-2 px-4 me-3 d-flex align-items-center"
                    style={{
                      fontSize: '15px',
                      borderRadius: '8px',
                      background: activeKey === 'dashboard' ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                      color: activeKey === 'dashboard' ? '#0ea5e9' : '#6b7280',
                      border: activeKey === 'dashboard' ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FaChartLine className="me-2" />
                    Dashboard
                  </Nav.Link>
                </Nav.Item>
                
                <Nav.Item>
                  <Nav.Link 
                    eventKey="transactions"
                    className="py-2 px-4 d-flex align-items-center"
                    style={{
                      fontSize: '15px',
                      borderRadius: '8px',
                      background: activeKey === 'transactions' ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                      color: activeKey === 'transactions' ? '#0ea5e9' : '#6b7280',
                      border: activeKey === 'transactions' ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <FaFileContract className="me-2" />
                    Transactions
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>
        </div>

        {/* Tab Content */}
        <Tab.Content>
          <Tab.Pane eventKey="dashboard">
            <EnhancedAgentDashboard />
          </Tab.Pane>
          
          <Tab.Pane eventKey="transactions">
            {showNewTransaction ? (
              <NewTransactionEnhanced 
                onBack={handleBackToTransactions}
                onTransactionCreated={handleTransactionCreated}
              />
            ) : selectedTransactionId ? (
              <TransactionDetails
                transactionId={selectedTransactionId}
                onBack={handleBackToTransactions}
                portal="agent"
              />
            ) : (
              <TransactionDashboard 
                onTransactionSelect={handleTransactionSelect}
                onNewTransaction={handleNewTransaction}
              />
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
};

export default AgentPortalStreamlined;